import { useEffect, useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { CreateInviteBodyKind, getApiErrorMessage } from "@/api";
import { AlertDialog } from "@/components/dialog/alert/AlertDialog";
import { FormButton } from "@/components/form/FormButton";
import { Icon } from "@/components/icons/Icon";
import { twMerge } from "@/utils/twMerge";

import type { FormButtonVariant } from "@/components/form/FormButton";
import type { TeamModel } from "@/models/TeamModel";

export const CreateInviteLinkButton = observer(function CreateInviteLinkButton({
  team,
  className,
  variant = "accent",
}: {
  team: TeamModel;
  className?: string;
  variant?: FormButtonVariant;
}) {
  const t = useTranslations();
  const [status, setStatus] = useState<"idle" | "creating-link" | "success" | "error">("idle");
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    if (status === "success") {
      setTimeout(() => {
        setStatus("idle");
      }, 3000);
    }
  }, [status]);

  if (!team) {
    return null;
  }

  const handleCreateInviteLinkClick = async () => {
    setServerError("");
    try {
      setStatus("creating-link");
      const { invite } = await team.invitesUX.createInvite({
        kind: CreateInviteBodyKind.reusable_link,
      });
      if (!invite) {
        throw new Error("Failed to create invite link");
      }
      const link = team.invitesUX.invites.find((i) => i.token === invite.token)?.link;
      if (link) {
        await navigator.clipboard.writeText(link);
      }
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setServerError(getApiErrorMessage(err));
    }
  };

  return (
    <>
      <FormButton
        isBusy={status === "creating-link"}
        variant={variant}
        size="sm"
        type="submit"
        disabled={status === "success"}
        className={twMerge("flex flex-row items-center justify-center", className)}
        onClick={handleCreateInviteLinkClick}
      >
        {status === "success" ? (
          t("teamDialog.createInviteLinkCopied")
        ) : (
          <>
            <Icon name="CopyLinkBlue" className="mr-1" />
            {t("teamDialog.createInviteLink")}
          </>
        )}
      </FormButton>
      <AlertDialog
        isOpen={status === "error"}
        title={t("teamDialog.createInviteLinkError")}
        description={serverError}
        onClose={() => {
          setServerError("");
          setStatus("idle");
        }}
      />
    </>
  );
});
